/**
 * Invitation titles, end-to-end encrypted (S-M1, review 2026-09-24).
 *
 * The inviter encrypts the map title under the map key; only the invitee,
 * after unwrapping the invitation's key with their own secret key, can read
 * it. The server stores and forwards the ciphertext. AAD binds it to the map,
 * so a title can't be moved to another invitation.
 *
 * Format: base64(iv[12] || AES-256-GCM(dek, utf8(title), aad)).
 */

const MAX_TITLE_LENGTH = 200;
const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });

const aad = docId => encoder.encode(`orbital:v1:invite-title:${docId}`);

function toBase64(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(text) {
  return Uint8Array.from(atob(text), c => c.charCodeAt(0));
}

async function importKey(dek, usage) {
  if (!(dek instanceof Uint8Array) || dek.length !== 32) throw new Error('Invalid map key');
  return crypto.subtle.importKey('raw', dek, { name: 'AES-GCM', length: 256 }, false, [usage]);
}

/** Visible characters only, at most 200 (the old server-side limit). */
export function normalizeTitle(title) {
  return [...String(title ?? '')].filter(c => !/\p{Cc}/u.test(c)).slice(0, MAX_TITLE_LENGTH).join('').trim();
}

/**
 * @param {Uint8Array} dek - the map key (32 bytes)
 * @param {string} docId
 * @param {string} title
 * @returns {Promise<string|null>} ciphertext, or null for an empty title
 */
export async function encryptInviteTitle(dek, docId, title) {
  const text = normalizeTitle(title);
  if (!text) return null;
  const key = await importKey(dek, 'encrypt');
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: aad(docId) }, key, encoder.encode(text)));
  const out = new Uint8Array(iv.length + ciphertext.length);
  out.set(iv);
  out.set(ciphertext, iv.length);
  return toBase64(out);
}

/**
 * @returns {Promise<string>} the title; throws if it doesn't decrypt for this map and key
 */
export async function decryptInviteTitle(dek, docId, encrypted) {
  const bytes = fromBase64(encrypted);
  if (bytes.length < 29) throw new Error('Invalid encrypted title');
  const key = await importKey(dek, 'decrypt');
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: bytes.subarray(0, 12), additionalData: aad(docId) }, key, bytes.subarray(12));
  return normalizeTitle(decoder.decode(plaintext));
}
